/* eslint-disable unicorn/filename-case -- TODO rename later */
import { authConfig } from "./oidc-config";

interface AccessToken {
  access_token: string;
  id_token: string;
  profile: { [key: string]: unknown };
  refresh_token: string;
  token_type: "Bearer";
}

interface ZitadelUserState {
  hash: string;
  key: string;
  pathname: string;
  search: string;
  state: null | string;
}

interface ZitadelUserProfile {
  aud: Array<string>;
  c_hash: string;
  client_id: string;
  email: string;
  email_verified: boolean;
  exp: number;
  family_name: string;
  given_name: string;
  iat: number;
  iss: string;
  locale: string | null;
  name: string;
  preferred_username: string;
  sub: string;
  updated_at: number;
}

interface ZitadelUserData {
  access_token: string;
  expires_at: number;
  id_token: string;
  profile: ZitadelUserProfile;
  refresh_token: string;
  scope: string;
  state?: ZitadelUserState;
  token_type: "Bearer";
}

export const istariUserLocalStorageKey = "istari-user";

export const AuthenticationStore = () => {
  const zitadelStoreKey = `oidc.user:${authConfig.authority}:${authConfig.client_id}`;
  const zitadelData: ZitadelUserData = JSON.parse(window.localStorage.getItem(zitadelStoreKey) as string);

  const userStr = window.localStorage.getItem(istariUserLocalStorageKey);
  const user = userStr ? JSON.parse(userStr) : {};

  function clearSessionToken() {
    window.localStorage.removeItem(zitadelStoreKey);
  }

  function getAccessToken(): string | undefined {
    return zitadelData?.access_token;
  }

  function getAccessTokenExpires(): number | null {
    return zitadelData?.expires_at;
  }

  function getCurrentUser(): { userId: string | undefined; userEmail: string | undefined } {
    const userId = user?.profile?.sub;
    const userEmail = user?.profile?.email;

    return {
      userId,
      userEmail,
    };
  }

  function getCurrentUserObject(): ZitadelUserData {
    return user;
  }

  function getCurrentUserOrganizationId(): string | null {
    return user.profile["urn:zitadel:iam:user:resourceowner:id"] || null;
  }

  function getRedirectPath(): string | null {
    const { pathname = "", search = "" } = user.state || {};
    if (pathname) {
      return pathname + search;
    }
    return null;
  }

  /**
   * Consumes and clears the redirect path to prevent infinite render loops in AuthManager.
   *
   * PROBLEM SOLVED:
   * After login, AuthManager would get stuck in an infinite redirect loop because:
   * 1. User logs in → localStorage gets updated with auth data including redirect path
   * 2. AuthManager reads redirectPath via getRedirectPath() → finds a path (e.g., "/")
   * 3. AuthManager renders <Navigate to={redirectPath} /> → triggers navigation
   * 4. Navigation causes re-render → getRedirectPath() still returns same path
   * 5. AuthManager renders <Navigate to={redirectPath} /> again → infinite loop
   * 6. <Outlet /> never gets rendered → app shows blank screen until manual refresh
   *
   * HOW THIS FIXES IT:
   * - consumeRedirectPath() reads the redirect path from localStorage once
   * - Gets fresh user data from localStorage (not stale cached data) to avoid overwrites
   * - Safely clears the path with null/undefined state protection using spread operator
   * - Updates both localStorage AND the in-memory user cache for consistency
   * - Returns the original path for one-time use by <Navigate />
   * - Subsequent calls return null since both localStorage and cache are cleared
   * - This allows AuthManager to redirect once, then render <Outlet /> normally
   *
   * This ensures stable authentication flow: redirect → clear → render app content.
   * Critical: Both localStorage and in-memory cache must be synchronized to prevent the loop.
   */
  function consumeRedirectPath(): string | null {
    const redirectPath = getRedirectPath();
    if (redirectPath) {
      const currentUserStr = window.localStorage.getItem(istariUserLocalStorageKey);
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : {};
      const clearedUser = {
        ...currentUser,
        state: {
          ...(currentUser.state || {}),
          pathname: "",
          search: "",
        },
      };
      window.localStorage.setItem(istariUserLocalStorageKey, JSON.stringify(clearedUser));
      Object.assign(user, clearedUser);
    }
    return redirectPath;
  }

  function getRefreshToken(): string {
    return zitadelData?.refresh_token;
  }

  function saveAccessToken(accessToken: AccessToken) {
    const { access_token, id_token, refresh_token } = accessToken;

    const newUserStr = {
      ...user,
      id_token,
      access_token,
      refresh_token,
    };
    window.localStorage.setItem(istariUserLocalStorageKey, JSON.stringify(newUserStr));
  }

  return {
    clearSessionToken,
    getAccessToken,
    getAccessTokenExpires,
    getCurrentUser,
    getCurrentUserObject,
    getCurrentUserOrganizationId,
    getRedirectPath,
    consumeRedirectPath,
    getRefreshToken,
    saveAccessToken,
  };
};
